import { useMemo } from 'react';
import {
  OverflowTooltip,
  AvatarGroup,
  ChipCounterList,
  Chip,
} from '@elementary/common-ui/components';
import dateRenderer from '@elementary/common-ui/components/cellRenderers/dateRenderer';
import testNameByPackageRenderer from '@elementary/common-ui/components/cellRenderers/testNameByPackageRenderer';
import testSourceTypeRenderer from '@elementary/common-ui/components/cellRenderers/testSourceTypeRenderer';
import { Column, ColumnType } from '@elementary/common-ui/components/Table';
import { capitalize, distinctArray } from '@elementary/common-ui/utils/common';
import { Test } from 'shared/types';
import AlertDestinationsTableCell from 'testManagement/components/AlertDestinationsTableCell';
import MuteTestToggleCell from 'testManagement/components/MuteTestToggleCell';
import { TestManagementTabs } from 'testManagement/types';
import dateFilterValueMatcher from '@elementary/common-ui/utils/comparers/dateFilterValueMatcher';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';

const configTypeLabelMap: Record<string, string> = {
  CLOUD: 'rule',
  CODE: 'code',
  CUSTOM: 'custom',
};

const useTestManagementTableColumns = (
  data: Test[],
  mode: TestManagementTabs,
) =>
  useMemo<Array<Column<Test>>>(() => {
    const baseColumns = [
      {
        id: 'name',
        header: 'Test name',
        renderer: testNameByPackageRenderer,
        filterDropdownWidth: 350,
      },
      {
        id: 'database_name',
        header: 'Database',
        defaultUntoggled: true,
      },
      {
        id: 'schema',
        header: 'Schema',
        renderer: (val: Test) => (
          <OverflowTooltip>{val.schema?.toLowerCase()}</OverflowTooltip>
        ),
        defaultUntoggled: true,
      },
      {
        id: 'table',
        header: 'Table',
        filterDropdownWidth: 350,
      },
      {
        id: 'column',
        header: 'Column',
        filterDropdownWidth: 350,
      },
      {
        id: 'source_type',
        header: 'Source',
        renderer: (val: Test) =>
          testSourceTypeRenderer(val.source_type || 'dbt'),
      },
      {
        id: 'is_custom_sql',
        header: 'Custom query',
        filterable: true,
        defaultUntoggled: true,
      },
    ];

    if (mode === 'alerts') {
      const alertDestinationsTypeFilterOptions = distinctArray(
        data.map((item) => item.alert_destinations_type || ''),
      ).map((item) => ({
        value: item,
        label: capitalize(configTypeLabelMap[item]),
      }));

      const alertDestinationsFilterOptions = distinctArray(
        data.flatMap((i) => i.channels),
      )
        .map((t) => ({ label: t, value: t }))
        .concat({ label: '', value: '' });

      return [
        ...baseColumns,
        {
          id: 'channels',
          header: 'Alert destinations',
          width: 220,
          filterable: true,
          filterOptionsOverride: alertDestinationsFilterOptions,
          renderer: ({ alert_destinations, muted }) => (
            <AlertDestinationsTableCell
              alertDestinations={alert_destinations}
              muted={!!muted}
            />
          ),
        },
        {
          id: 'alert_destinations_type',
          header: 'Alert Type',
          filterable: true,
          width: 164,
          filterOptionsOverride: alertDestinationsTypeFilterOptions,
          renderer: ({ alert_destinations_type, muted }) => (
            <Chip
              sx={{
                opacity: muted ? 0.3 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
              variant="outlined"
              label={configTypeLabelMap[alert_destinations_type!].toUpperCase()}
              size="small"
              bold
            />
          ),
        },
        {
          id: 'muted',
          width: 148,
          header: 'Alerts',
          filterOptionsOverride: [
            { label: 'Disabled', value: 'true' },
            { label: 'Enabled', value: 'false' },
          ],
          renderer: ({ id, muted }) => (
            <MuteTestToggleCell id={id} muted={!!muted} />
          ),
        },
      ];
    }

    if (mode === 'configuration') {
      return [
        ...baseColumns,
        {
          id: 'severity',
          header: 'Severity',
          renderer: (val) => (
            <OverflowTooltip>{capitalize(val.severity)}</OverflowTooltip>
          ),
        },
        {
          id: 'owners',
          header: 'Owners',
          width: 148,
          filterOptionsOverride: distinctArray(data?.flatMap((i) => i.owners))
            .map((t) => ({ label: t, value: t }))
            .concat({ label: '', value: '' }),
          renderer: ({ owners }) => <AvatarGroup items={owners} />,
        },
        {
          id: 'tags',
          header: 'Tags',
          width: 160,
          filterOptionsOverride: distinctArray(data?.flatMap((i) => i.tags))
            .map((t) => ({ label: t, value: t }))
            .concat({ label: '', value: '' }),
          renderer: ({ tags }) => <ChipCounterList items={tags} />,
        },
        {
          id: 'last_data_modification_time',
          header: 'Last table update',
          type: ColumnType.Date,
          width: 200,
          renderer: (val) =>
            dateRenderer(val.last_data_modification_time || ''),
          emptyValueLabel: 'No update time',
          filterValueMatcher: ([from, to, emptyFlag], rowData) =>
            dateFilterValueMatcher(
              from,
              to,
              emptyFlag,
              rowData.last_data_modification_time,
            ),
          compareFunc: (a, b) =>
            dateComparer(
              a.last_data_modification_time,
              b.last_data_modification_time,
            ),
        },
        {
          id: 'materialization',
          header: 'Materialization',
          width: 200,
          renderer: (val) => (
            <OverflowTooltip>
              {capitalize(val.materialization || '')}
            </OverflowTooltip>
          ),
          defaultUntoggled: true,
        },
        {
          id: 'updated_at',
          header: 'Last test update',
          renderer: (val) => dateRenderer(val.updated_at),
          type: ColumnType.Date,
          width: 200,
          defaultUntoggled: true,
          filterValueMatcher: ([from, to, emptyFlag], rowData) =>
            dateFilterValueMatcher(from, to, emptyFlag, rowData.updated_at),
          compareFunc: (a, b) => dateComparer(a.updated_at, b.updated_at),
        },
      ];
    }
    return baseColumns;
  }, [data, mode]);

export default useTestManagementTableColumns;
